import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { FerrisWheelSpinner, WindmillSpinnerOverlay } from 'react-spinner-overlay';
import Swal from "sweetalert2";
import './Halltickets.css';
import NotFoundImage from './not-found.png';
import API_URL from '../../api/ExamCalendarApi';

const HallTickets = () => {
  const [allHallTickets, setAllHallTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  
  const [selectedTickets, setSelectedTickets] = useState(new Set());
  const entriesPerPage = 15;


  const getAuthToken = () => localStorage.getItem("authToken");

  const fetchAllHallTickets = async () => {
    setLoading(true);
    setError("");
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API_URL}/hallticket/getAllHallTickets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
  
      // Sort hall tickets by createdAt in descending order (newest first)
      const sortedHallTickets = response.data.allHallTickets.sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
  
      setAllHallTickets(sortedHallTickets);
    } catch (error) {
      setError("Failed to fetch hall tickets");
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchAllHallTickets();
  }, []);
  

  const flattenedHallTickets = useMemo(() => 
    allHallTickets.flatMap((ticketDoc) =>
      ticketDoc.hallTickets.map((ticket) => ({
        ...ticketDoc,
        ...ticket,
      }))
    ), [allHallTickets]
  );

  // Extract unique months and years from dates
  const availableMonths = useMemo(() => {
    const months = new Set();
    flattenedHallTickets.forEach(ticket => {
      const date = new Date(ticket.dateOfJoining);
      if (!isNaN(date)) {
        months.add(date.toLocaleString('default', { month: 'long' }));
      }
    });
    return Array.from(months).sort();
  }, [flattenedHallTickets]);

  const availableYears = useMemo(() => {
    const years = new Set();
    flattenedHallTickets.forEach(ticket => {
      const date = new Date(ticket.dateOfJoining);
      if (!isNaN(date)) {
        years.add(date.getFullYear());
      }
    });
    return Array.from(years).sort((a, b) => a - b);
  }, [flattenedHallTickets]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const exportToExcel = () => {
    // Use filteredTickets to export the filtered data
    const data = filteredTickets.map((ticket) => ({
      "Student ID": ticket.studentId,
      "Name": ticket.name,
      "Date of Birth": formatDate(ticket.dob),
      "Aadhar Number": ticket.aadharNo,
      "Gender": ticket.gender,
      "District/Zone": ticket.cityName,
      "Email": ticket.email,
      "Mobile Number": ticket.parentMobileNo,
      "Date of Joining": formatDate(ticket.dateOfJoining),
      "Date of Expiry": formatDate(ticket.expiryDate),
      "Exam Name": ticket.examName,
      "Hall Ticket Numbers": ticket.hallTicketNumber,
      "Center": ticket.centre,
    }));
  
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Hall Tickets");
    XLSX.writeFile(wb, "hall_tickets.xlsx");
  };
  
  // Filtering and sorting logic
const filteredTickets = useMemo(() => {
  let result = flattenedHallTickets;

  // Search query filter
  if (searchQuery) {
    result = result.filter((ticket) =>
      Object.values(ticket)
        .join(" ")
        .toLowerCase()
        .trim()
        .includes(searchQuery.toLowerCase().trim())
    );
  }

  // Month filter
  if (selectedMonth) {
    result = result.filter(ticket => {
      const date = new Date(ticket.dateOfJoining);
      return date.toLocaleString('default', { month: 'long' }) === selectedMonth;
    });
  }

  // Year filter
  if (selectedYear) {
    result = result.filter(ticket => {
      const date = new Date(ticket.dateOfJoining);
      return date.getFullYear() === parseInt(selectedYear);
    });
  }

  // Sort by creation date (recent first)
  return result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
}, [flattenedHallTickets, searchQuery, selectedMonth, selectedYear]);


  // Pagination logic
  const indexOfLastTicket = currentPage * entriesPerPage;
  const indexOfFirstTicket = indexOfLastTicket - entriesPerPage;
  const currentTickets = filteredTickets.slice(indexOfFirstTicket, indexOfLastTicket);
  const totalPages = Math.ceil(filteredTickets.length / entriesPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleResendEmail = async (ticket) => {
    Swal.fire({
      title: "Single Email Only",
      text: "Multiple email resend is not supported. Please select and resend emails one at a time.",
      icon: "info",
      confirmButtonText: "Continue with single email",
      showCancelButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        setProcessing(true);
        axios.get(
          `${API_URL}/hallticket/getHallTicketByStudentIdAndNumber/${ticket.studentId}/${ticket.hallTicketNumber}`,
          {
            headers: { Authorization: `Bearer ${getAuthToken()}` },
          }
        )
          .then(() => Swal.fire("Success", "Email resent successfully!", "success"))
          .catch(() => Swal.fire("Error", "Failed to resend email.", "error"))
          .finally(() => setProcessing(false));
      }
    });
  };


  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(currentTickets.map(ticket => ticket.hallTicketNumber));
      setSelectedTickets(newSelected);
    } else {
      setSelectedTickets(new Set());
    }
  };

  const handleSelectTicket = (hallTicketNumber) => {
    const newSelected = new Set(selectedTickets);
    if (newSelected.has(hallTicketNumber)) {
      newSelected.delete(hallTicketNumber);
    } else {
      newSelected.add(hallTicketNumber);
    }
    setSelectedTickets(newSelected);
  };

  const handleDownloadMultiple = async () => {
    if (selectedTickets.size === 0) {
      Swal.fire("Warning", "Please select at least one hall ticket to download.", "warning");
      return;
    }
  
    setProcessing(true);
    try {
      // Prepare the list of selected tickets
      const tickets = Array.from(selectedTickets).map(hallTicketNumber => {
        const ticket = currentTickets.find(t => t.hallTicketNumber === hallTicketNumber);
        return { studentId: ticket.studentId, hallTicketNumber };
      });
  
      // Send a POST request to the bulk download endpoint
      const response = await axios.post(
        `${API_URL}/hallticket/downloadMultipleHallTickets`,
        { tickets },
        {
          responseType: "blob", // Important for handling binary data
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        }
      );
  
      // Trigger the download of the ZIP file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "HallTickets.zip");
      document.body.appendChild(link);
      link.click();
  
      Swal.fire("Success", `${selectedTickets.size} hall tickets downloaded successfully!`, "success");
      setSelectedTickets(new Set()); // Clear selection after download
    } catch (error) {
      Swal.fire("Error", "Failed to download some hall tickets.", "error");
    } finally {
      setProcessing(false);
    }
  };
  const handleDownloadHallTicket = async (ticket, showMessage = true) => {
    try {
      setProcessing(true); // Start loading
      const response = await axios.get(
        `${API_URL}/hallticket/downloadHallTicket/${ticket.studentId}/${ticket.hallTicketNumber}`,
        {
          responseType: "blob",
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `HallTicket_${ticket.hallTicketNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      if (showMessage) {
        Swal.fire("Success", "Hall ticket downloaded successfully!", "success");
      }
    } catch {
      Swal.fire("Error", "Failed to download hall ticket.", "error");
    } finally {
      setProcessing(false); // Stop loading
    }
  };
  const handleDeleteHallTicket = async (ticket) => {
    const confirmed = await Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this hall ticket!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
    });

    if (confirmed.isConfirmed) {
      setProcessing(true);
      try {
        await axios.delete(
          `${API_URL}/hallticket/deleteHallTicketById/${ticket.studentId}/${ticket.hallTicketNumber}`,
          {
            headers: { Authorization: `Bearer ${getAuthToken()}` },
          }
        );
        Swal.fire("Deleted!", "The hall ticket has been deleted.", "success");
        fetchAllHallTickets(); // Refresh the list after deletion
      } catch (error) {
        Swal.fire("Error", "Failed to delete hall ticket.", "error");
      } finally {
        setProcessing(false);
      }
    }
  };

  return (
    <div className="admin-halltickets-container">
      <h1 className="admin-halltickets-title">Hall Tickets</h1>
      {loading && <FerrisWheelSpinner loading={loading} size={28} />}
      {error && <div className="admin-halltickets-alert">{error}</div>}
      <div className="admin-halltickets-filters">
        <input
          type="text"
          placeholder="Search by Hallticket Number or Name or Student ID"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="admin-halltickets-search"
        />
        <div className="filters">
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          className="admin-halltickets-dropdown"
        >
          <option value="">All Months</option>
          {availableMonths.map(month => (
            <option key={month} value={month}>{month}</option>
          ))}
        </select>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.target.value)}
          className="admin-halltickets-dropdown"
        >
          <option value="">All Years</option>
          {availableYears.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
        <button onClick={exportToExcel} className="admin-halltickets-export">
          Export to Excel
        </button>
        <button
          onClick={handleDownloadMultiple}
          className="admin-halltickets-export"
          disabled={selectedTickets.size === 0}
        >
          Download Selected ({selectedTickets.size})
        </button>
        </div>
      </div>
      {processing && (
        <WindmillSpinnerOverlay
          loading={processing}
          overlayColor="rgba(0, 123, 255, 0.1)"
          color="#007BFF"
        />
      )}
      <div className="admin-halltickets-table-wrapper">
        {filteredTickets.length === 0 ? (
          <div className="admin-halltickets-no-data">
            <h3 className="no-data">No data found. Please search with a valid query.</h3>
            <img className="not-found" src={NotFoundImage} alt="No data found" />
          </div>
        ) : (
          <table className="admin-halltickets-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={currentTickets.length > 0 && selectedTickets.size === currentTickets.length}
                  />
                </th>
                <th>Student ID</th>
                <th>Name</th>
                <th>HallTicketNumber</th>
                <th>Date of Joining</th>
                <th>Date of Expiry</th>
                <th>Exam Name</th>
                <th>Registration Form</th>
                <th>Resend Email</th>
                <th>Download</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {currentTickets.map((ticket) => (
                <tr key={ticket.hallTicketNumber}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedTickets.has(ticket.hallTicketNumber)}
                      onChange={() => handleSelectTicket(ticket.hallTicketNumber)}
                    />
                  </td>
                  <td>{ticket.studentId}</td>
                  <td>{ticket.name}</td>
                  <td>{ticket.hallTicketNumber}</td>
                  <td>{formatDate(ticket.dateOfJoining)}</td>
                  <td>{formatDate(ticket.expiryDate)}</td>
                  <td>{ticket.examName}</td>
                  <td>
                    {ticket.registrationForm ? (
                      <a href={ticket.registrationForm} target="_blank" rel="noopener noreferrer">
                        View PDF
                      </a>
                    ) : (
                      "No URL"
                    )}
                  </td>
                  <td>
                    <button onClick={() => handleResendEmail(ticket)} className="button-21">
                      Resend Email
                    </button>
                  </td>
                  <td>
                    <button onClick={() => handleDownloadHallTicket(ticket, true)} className="button-21">
                      Download
                    </button>
                  </td>
                  <td>
                    <button
                      onClick={() => handleDeleteHallTicket(ticket)}
                      className="button-21 delete-button"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="admin-halltickets-pagination">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="admin-halltickets-pagination-prev"
        >
          Prev
        </button>
        {[...Array(totalPages)].map((_, index) => (
          <button
            key={index + 1}
            onClick={() => handlePageChange(index + 1)}
            className={`admin-halltickets-pagination-item ${index + 1 === currentPage ? "active" : ""}`}
          >
            {index + 1}
          </button>
        ))}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="admin-halltickets-pagination-next"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default HallTickets;